import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { registrationGuard } from './guards/registration.guard';
import { featureGuard } from './guards/feature.guard';

export const routes: Routes = [
  // Default redirect
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },

  // Auth routes — no guards
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login.component').then(m => m.LoginComponent),
      },
      {
        path: 'register',
        loadComponent: () =>
          import('./features/auth/register.component').then(m => m.RegisterComponent),
      },
      {
        path: 'verify-email',
        loadComponent: () =>
          import('./features/auth/verify-email.component').then(m => m.VerifyEmailComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('./features/auth/forgot-password.component').then(m => m.ForgotPasswordComponent),
      },
      {
        path: 'callback',
        loadComponent: () =>
          import('./features/auth/auth-callback.component').then(m => m.AuthCallbackComponent),
      },
    ],
  },

  // Registration route — AuthGuard only (user must be logged in but not yet registered)
  {
    path: 'registration',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/registration/profile-completion.component').then(
        m => m.ProfileCompletionComponent
      ),
  },

  // Feature routes — AuthGuard + RegistrationGuard
  {
    path: 'dashboard',
    canActivate: [authGuard, registrationGuard],
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
  },
  {
    path: 'resource-types',
    canActivate: [authGuard, registrationGuard],
    loadComponent: () =>
      import('./features/resource-types/resource-types.component').then(
        m => m.ResourceTypesComponent
      ),
  },
  {
    path: 'resources',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/resources/resource-management.component').then(
        m => m.ResourceManagementComponent
      ),
  },
  {
    path: 'job-requirements',
    redirectTo: '/dashboard',
    pathMatch: 'full',
  },
  {
    path: 'virtual-workers',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/virtual-workers/virtual-workers.component').then(
        m => m.VirtualWorkersComponent
      ),
  },
  {
    path: 'virtual-workers/:virtualWorkerId/training',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/virtual-workers/training/training.component').then(
        m => m.TrainingComponent
      ),
  },
  {
    path: 'availability',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/availability/availability.component').then(
        m => m.AvailabilityComponent
      ),
  },
  {
    path: 'clients',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/clients/client-management.component').then(
        m => m.ClientManagementComponent
      ),
  },
  {
    path: 'clients/tickets',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/clients/tickets/client-tickets.component').then(
        m => m.ClientTicketsComponent
      ),
  },
  {
    path: 'services',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/services/service-catalogue.component').then(
        m => m.ServiceCatalogueComponent
      ),
  },
  {
    path: 'bookings',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/bookings/bookings.component').then(m => m.BookingsComponent),
  },
  {
    path: 'field-service',
    redirectTo: '/bookings',
    pathMatch: 'full',
  },
  {
    path: 'allocation',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/allocation/allocation.component').then(
        m => m.AllocationComponent
      ),
  },
  {
    path: 'reports',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/reports/reports.component').then(m => m.ReportsComponent),
  },
  {
    path: 'audit',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/audit/audit.component').then(m => m.AuditComponent),
  },
  {
    path: 'billing',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/billing/billing.component').then(m => m.BillingComponent),
  },
  {
    path: 'invoices',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/invoices/invoices.component').then(m => m.InvoicesComponent),
  },
  {
    path: 'invoices/:id',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/invoices/invoice-detail.component').then(m => m.InvoiceDetailComponent),
  },
  {
    path: 'quotes',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/quotes/quotes.component').then(m => m.QuotesComponent),
  },
  {
    path: 'quotes/create',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/quotes/quote-create.component').then(m => m.QuoteCreateComponent),
  },
  {
    path: 'quotes/:id',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/quotes/quote-detail.component').then(m => m.QuoteDetailComponent),
  },
  {
    path: 'work-orders',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/work-orders/work-orders.component').then(m => m.WorkOrdersComponent),
  },
  {
    path: 'work-orders/:id',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/work-orders/work-order-detail.component').then(
        m => m.WorkOrderDetailComponent
      ),
  },
  {
    path: 'ai-managers',
    canActivate: [authGuard, registrationGuard, featureGuard],
    loadComponent: () =>
      import('./features/ai-managers/ai-managers.component').then(
        m => m.AiManagersComponent
      ),
  },
  {
    path: 'settings',
    canActivate: [authGuard, registrationGuard],
    loadComponent: () =>
      import('./features/settings/settings.component').then(m => m.SettingsComponent),
  },

  // Wildcard redirect
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
